/**
 * MinigameManager — registry + lifecycle for skill-check minigames.
 *
 * Manages a pool of registered minigames, picks one at random on start,
 * drives tick/input/render, and converts final score into a GURPS roll bonus.
 */

/** Opaque state bag — each minigame defines its own shape. */
export type MinigameState = Record<string, unknown>;

/** Interface every minigame must implement. */
export interface Minigame {
  /** Unique identifier (e.g. "rune-catcher"). */
  id: string;
  /** Human-readable display name. */
  name: string;
  /** Create initial state for a fresh round. */
  init(): MinigameState;
  /** Advance game by `dt` milliseconds. */
  tick(dt: number, state: MinigameState): MinigameState;
  /** Handle player input (Space key OR tap/click). Optional key for multi-key games. */
  onInput(state: MinigameState, key?: string): MinigameState;
  /** True when the game has ended (timeout or win). */
  isComplete(state: MinigameState): boolean;
  /** Final score in range 0-100. */
  score(state: MinigameState): number;
  /** Render the game into `height` lines, each exactly `width` chars wide. */
  render(width: number, height: number, state: MinigameState): string[];
}

/** Result returned when all minigames are dismissed. */
export interface MinigameResult {
  /** Last game played. */
  gameId: string;
  /** Average score across all games played 0-100. */
  score: number;
  /** GURPS roll bonus derived from average score. */
  bonus: number;
  /** How many games were completed during this loading session. */
  gamesPlayed: number;
}

/**
 * Cycling phase: "playing" → game ends → "result" (350ms answer reveal) → auto-start next.
 * When externally dismissed, accumulates all scores into a single MinigameResult.
 */
type CyclePhase = "playing" | "result";

/** Delay in ms to show the result before cycling to next game. */
const RESULT_DISPLAY_MS = 350;

export class MinigameManager {
  private games: Minigame[] = [];
  private current: Minigame | null = null;
  private state: MinigameState | null = null;
  private _phase: CyclePhase = "playing";
  private resultTimer = 0;
  private lastGameId: string | null = null;

  /** Score accumulation across all games in one loading session. */
  private scores: number[] = [];
  private _lastScore = 0;

  /** Register a minigame into the pool. */
  register(game: Minigame): void {
    this.games.push(game);
  }

  /** Pick a random registered game and start it. No-op if registry is empty. */
  start(): void {
    if (this.games.length === 0) return;
    this.startNextGame();
  }

  /** Pick a random game different from the last one played (if possible). */
  private startNextGame(): void {
    if (this.games.length === 0) return;

    let candidates = this.games;
    if (this.games.length > 1 && this.lastGameId) {
      candidates = this.games.filter((g) => g.id !== this.lastGameId);
    }
    const idx = Math.floor(Math.random() * candidates.length);
    this.current = candidates[idx];
    this.state = this.current.init();
    this.lastGameId = this.current.id;
    this._phase = "playing";
    this.resultTimer = 0;
  }

  /** Advance the active game by `dt` milliseconds. Handles cycling. */
  tick(dt: number): void {
    if (!this.current || !this.state) return;

    if (this._phase === "result") {
      // Counting down the 350ms result display
      this.resultTimer += dt;
      if (this.resultTimer >= RESULT_DISPLAY_MS) {
        this.startNextGame();
      }
      return;
    }

    // Playing phase — tick the game
    this.state = this.current.tick(dt, this.state);

    // Check if the game ended naturally (timeout, completion)
    if (this.current.isComplete(this.state)) {
      this._lastScore = this.current.score(this.state);
      this.scores.push(this._lastScore);
      this._phase = "result";
      this.resultTimer = 0;
    }
  }

  /** Forward player input to the active game. No-op during result phase. */
  input(key?: string): void {
    if (!this.current || !this.state) return;
    if (this._phase !== "playing") return;
    this.state = this.current.onInput(this.state, key);

    // Check if the input completed the game (e.g. quiz answered)
    if (this.current.isComplete(this.state)) {
      this._lastScore = this.current.score(this.state);
      this.scores.push(this._lastScore);
      this._phase = "result";
      this.resultTimer = 0;
    }
  }

  /** Render the active game. During result phase, appends score line. */
  render(width: number, height: number): string[] {
    if (!this.current || !this.state) {
      return Array.from({ length: height }, () => " ".repeat(width));
    }
    const lines = this.current.render(width, height - 1, this.state);

    // Add status line at bottom
    let statusLine: string;
    if (this._phase === "result") {
      const emoji = this._lastScore >= 50 ? "+" : "x";
      statusLine = `[${emoji}] Score: ${this._lastScore} | Games: ${this.scores.length}`;
    } else {
      statusLine = `Games: ${this.scores.length} | Best bonus: +${this.scoreToBonus(this.bestScore())}`;
    }
    // Pad or truncate to width
    lines.push(statusLine.slice(0, width).padEnd(width));

    return lines;
  }

  /** Stop all games, compute aggregate result. */
  dismiss(): MinigameResult {
    // If currently playing, count the in-progress score too
    if (this.current && this.state && this._phase === "playing") {
      this.scores.push(this.current.score(this.state));
    }

    const avgScore =
      this.scores.length > 0
        ? Math.round(this.scores.reduce((a, b) => a + b, 0) / this.scores.length)
        : 0;
    const result: MinigameResult = {
      gameId: this.lastGameId ?? "",
      score: avgScore,
      bonus: this.scoreToBonus(this.bestScore()),
      gamesPlayed: this.scores.length,
    };
    this.current = null;
    this.state = null;
    this._phase = "playing";
    this.scores = [];
    return result;
  }

  /** Whether games are active (playing or showing result). */
  isActive(): boolean {
    return this.current !== null;
  }

  /** The id of the current game, or null. */
  currentGameId(): string | null {
    return this.current?.id ?? null;
  }

  /** The display name of the current game. */
  currentGameName(): string {
    return this.current?.name ?? "";
  }

  /** Current cycling phase. */
  phase(): CyclePhase {
    return this._phase;
  }

  /** Best score across all games in this session. */
  private bestScore(): number {
    return this.scores.length > 0 ? Math.max(...this.scores) : 0;
  }

  /** Convert a 0-100 score into a GURPS roll bonus (0-3). */
  scoreToBonus(score: number): number {
    if (score >= 76) return 3;
    if (score >= 51) return 2;
    if (score >= 26) return 1;
    return 0;
  }
}
